import type { ClipboardEvent } from 'react'

/**
 * Text copied out of a PDF, a Word document or a web page carries a hard
 * newline at the end of every *rendered* line. Pasted verbatim into a textarea
 * those breaks survive, so the message keeps the width of the column it was
 * copied from and leaves a ragged gap down the right of the input box.
 *
 * `unwrapPastedText` rejoins the lines that only exist because the source
 * wrapped, and leaves deliberate structure — blank-line paragraph breaks, list
 * items, headings, quotes, indented blocks — exactly as it was.
 */

// A line starting with any of these begins a new block, so the break before it
// is deliberate: bullets, numbered/lettered items, markdown headings, quotes.
const BLOCK_START =
  /^\s*(?:[-*•–—‣]\s|\(?[0-9]{1,3}[.)]\s|\(?[a-zA-Z][.)]\s|#{1,6}\s|>)/

// Two or more leading spaces (or a tab) reads as preformatted — code, an ASCII
// table, a hanging indent. Never reflow those.
const INDENTED = /^(?:[ ]{2,}|\t)/

// Below this a line is short enough to be a deliberate break (a title, a
// reference number, a one-line list item) rather than a wrap point.
const MIN_WRAPPED_LINE_LENGTH = 40

function shouldJoin(previous: string, next: string): boolean {
  const previousTrimmed = previous.trim()
  const nextTrimmed = next.trim()

  if (!previousTrimmed || !nextTrimmed) return false
  if (INDENTED.test(previous) || INDENTED.test(next)) return false
  if (BLOCK_START.test(next)) return false
  if (previousTrimmed.length < MIN_WRAPPED_LINE_LENGTH) return false

  return true
}

export function unwrapPastedText(text: string): string {
  const normalised = text.replace(/\r\n?/g, '\n')
  if (!normalised.includes('\n')) return normalised

  const [first = '', ...rest] = normalised.split('\n')
  let result = first
  let previous = first

  for (const line of rest) {
    if (shouldJoin(previous, line)) {
      result = `${result.replace(/[ \t]+$/, '')} ${line.trimStart()}`
    } else {
      result += `\n${line}`
    }
    previous = line
  }

  return result
}

/**
 * Replace the textarea's current selection with `text`, going through the
 * native value setter so React's onChange still fires. `insertText` is tried
 * first because it keeps the browser's own undo stack intact.
 */
function insertAtCursor(el: HTMLTextAreaElement, text: string): void {
  if (typeof document.execCommand === 'function' && document.execCommand('insertText', false, text)) {
    return
  }

  const start = el.selectionStart ?? el.value.length
  const end = el.selectionEnd ?? start
  const nextValue = el.value.slice(0, start) + text + el.value.slice(end)

  // eslint-disable-next-line @typescript-eslint/unbound-method -- invoked below with .call(el, …)
  const setValue = Object.getOwnPropertyDescriptor(HTMLTextAreaElement.prototype, 'value')?.set
  if (setValue) setValue.call(el, nextValue)
  else el.value = nextValue

  const caret = start + text.length
  el.setSelectionRange(caret, caret)
  el.dispatchEvent(new Event('input', { bubbles: true }))
}

/**
 * `onPaste` handler for the AI input boxes. Falls through to the browser's own
 * paste whenever there is nothing to reflow, so plain typing-speed pastes and
 * image pastes are untouched.
 */
export function handleUnwrappingPaste(event: ClipboardEvent<HTMLTextAreaElement>): void {
  const pasted = event.clipboardData?.getData('text/plain')
  if (!pasted || !pasted.includes('\n')) return

  const unwrapped = unwrapPastedText(pasted)
  if (unwrapped === pasted) return

  event.preventDefault()
  insertAtCursor(event.currentTarget, unwrapped)
}
